import type { ColumnDef } from '@tanstack/react-table';
import type { LucideIcon } from 'lucide-react';
import {
  Receipt, ShoppingBag, Wallet, FileText, Users, Building2, Package, Warehouse,
  ListTree, Tag, Briefcase, Settings, Wrench, ClipboardList,
  Sparkles, BarChart3, Landmark, Ruler,
  BookOpen, TrendingDown, CheckSquare, Clock, ListChecks,
  Gauge, Boxes, Truck, UserCheck, SplitSquareHorizontal,
  Factory, Layers, FileBarChart,
} from 'lucide-react';
import { money, date, humanizeEnum } from './format';
import { DocstatusPill, StatusPill } from '@/components/StatusPill';
import { Link } from '@tanstack/react-router';

/**
 * DoctypeConfig — the minimal record a generic ListView needs to render any doctype.
 * Per the brief §3.1 — metadata-driven where useful, hand-built where UX matters.
 */
export interface DoctypeConfig<T = any> {
  // routing + UI labels
  slug: string;                 // URL path under the module, e.g. 'sales-invoices'
  modulePath: string;           // module URL path, e.g. '/accounting'
  module: string;               // human module name
  doctype: string;              // server doctype, e.g. 'sales_invoice'
  title: string;                // plural display
  singular: string;             // singular display
  icon: LucideIcon;

  // server
  endpoint: string;             // GET-list endpoint relative to /api/v1, e.g. '/accounting/sales-invoices'

  // list view
  columns: ColumnDef<T>[];
  newPath?: string;             // optional: override "new" link path (defaults to {modulePath}/{slug}/new)
  hasNew?: boolean;             // show "New" toolbar action (default true)
}

// ---- column factories shared across doctypes ----

const codeCol = (key: string, header = 'Code'): ColumnDef<any> => ({
  accessorKey: key,
  header,
  cell: (info) => <span className="font-mono text-dense font-medium text-text-primary">{info.getValue<string>()}</span>,
});

const nameCol = (key = 'name', header = 'Name', icon?: LucideIcon): ColumnDef<any> => ({
  accessorKey: key,
  header,
  cell: (info) => (
    <div className="flex items-center gap-2.5 min-w-0">
      {icon ? (
        <div className="size-7 rounded-md bg-surface text-brand-green-ink inline-flex items-center justify-center shrink-0">
          {(() => { const I = icon; return <I className="size-3.5" />; })()}
        </div>
      ) : null}
      <span className="text-text-primary truncate">{info.getValue<string>()}</span>
    </div>
  ),
});

const dateCol = (key: string, header = 'Date'): ColumnDef<any> => ({
  accessorKey: key,
  header,
  cell: (info) => <span className="text-text-secondary">{date(info.getValue<string>())}</span>,
});

const moneyCol = (key: string, header: string): ColumnDef<any> => ({
  accessorKey: key,
  header,
  meta: { align: 'right' },
  cell: (info) => <span className="font-medium num">{money(info.getValue<string>())}</span>,
});

const docstatusCol: ColumnDef<any> = {
  accessorKey: 'docstatus',
  header: 'Doc status',
  cell: (info) => <DocstatusPill docstatus={info.getValue<number>()} />,
};

// Party key -> detail-page URL builder. Names in list cells become links to
// the referenced record (customer/supplier/asset/employee) instead of bare IDs.
const PARTY_LINK: Record<string, (id: string) => string> = {
  customer_id: (id) => `/accounting/customers/${id}`,
  supplier_id: (id) => `/accounting/suppliers/${id}`,
  asset_id:    (id) => `/assets/assets/${id}`,
  employee_id: (id) => `/hr/employees/${id}`,
  project_id:  (id) => `/projects/projects/${id}`,
};

const partyCol = (key: string, header: string, nameKey?: string): ColumnDef<any> => ({
  accessorKey: key,
  header,
  cell: (info) => {
    const row = info.row.original as Record<string, unknown>;
    const display = nameKey ? (row[nameKey] as string | undefined) : undefined;
    const id = info.getValue<string>();
    const label = display || id || '—';
    const build = PARTY_LINK[key];
    if (id && build) {
      return (
        <Link
          to={build(id) as never}
          onClick={(e) => e.stopPropagation()}
          className="text-text-primary hover:text-accent hover:underline underline-offset-2"
        >
          {label}
        </Link>
      );
    }
    return <span className="text-text-primary">{label}</span>;
  },
});

// ---- configs ----

const items: DoctypeConfig = {
  slug: 'items', modulePath: '/accounting', module: 'Inventory',
  doctype: 'item', title: 'Items', singular: 'Item', icon: Package,
  endpoint: '/accounting/items',
  columns: [
    codeCol('code'),
    nameCol('name', 'Name', Package),
    { id: 'interchange_no', accessorKey: 'interchange_no', header: 'Interchange / OEM', cell: (i) => <span className="font-mono text-dense text-text-secondary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'stock_uom', header: 'UOM', cell: (i) => <span className="text-text-secondary">{i.getValue<string>()}</span> },
    {
      id: 'flags', header: 'Flags',
      cell: (i) => (
        <div className="flex items-center gap-1">
          {i.row.original.is_stock_item    && <StatusPill tone="info" withDot={false}>Stock</StatusPill>}
          {i.row.original.is_sales_item    && <StatusPill tone="success" withDot={false}>Sales</StatusPill>}
          {i.row.original.is_purchase_item && <StatusPill tone="accent" withDot={false}>Purchase</StatusPill>}
        </div>
      ),
    },
    moneyCol('standard_rate', 'Standard rate'),
  ],
};

const customers: DoctypeConfig = {
  slug: 'customers', modulePath: '/accounting', module: 'Sales',
  doctype: 'customer', title: 'Customers', singular: 'Customer', icon: Users,
  endpoint: '/accounting/customers',
  columns: [
    nameCol('display_name', 'Customer', Users),
    { accessorKey: 'npwp', header: 'NPWP', cell: (i) => <span className="font-mono text-dense text-text-secondary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'is_individual', header: 'Type', cell: (i) => <StatusPill tone="neutral" withDot={false}>{i.getValue<boolean>() ? 'Individual' : 'Entity'}</StatusPill> },
    { accessorKey: 'email', header: 'Email', cell: (i) => <span className="text-text-secondary">{i.getValue<string>() || '—'}</span> },
  ],
};

const suppliers: DoctypeConfig = {
  slug: 'suppliers', modulePath: '/accounting', module: 'Procurement',
  doctype: 'supplier', title: 'Suppliers', singular: 'Supplier', icon: Building2,
  endpoint: '/accounting/suppliers',
  columns: [
    nameCol('display_name', 'Supplier', Building2),
    { accessorKey: 'npwp', header: 'NPWP', cell: (i) => <span className="font-mono text-dense text-text-secondary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'is_individual', header: 'Type', cell: (i) => <StatusPill tone="neutral" withDot={false}>{i.getValue<boolean>() ? 'Individual' : 'Entity'}</StatusPill> },
    { accessorKey: 'email', header: 'Email', cell: (i) => <span className="text-text-secondary">{i.getValue<string>() || '—'}</span> },
  ],
};

const taxTemplates: DoctypeConfig = {
  slug: 'tax-templates', modulePath: '/accounting', module: 'Finance',
  doctype: 'tax_template', title: 'Tax Templates', singular: 'Tax Template', icon: Tag,
  endpoint: '/accounting/tax-templates',
  columns: [
    nameCol('name', 'Name', Tag),
    { accessorKey: 'is_sales', header: 'Side', cell: (i) =>
        i.getValue<boolean>() ? <StatusPill tone="success" withDot={false}>Sales</StatusPill> : <StatusPill tone="info" withDot={false}>Purchase</StatusPill> },
    { accessorKey: 'is_default', header: 'Default', cell: (i) => i.getValue<boolean>() ? <StatusPill tone="accent" withDot={false}>Default</StatusPill> : <span className="text-text-tertiary">—</span> },
    dateCol('created_at', 'Created'),
  ],
};

const accounts: DoctypeConfig = {
  slug: 'accounts', modulePath: '/accounting', module: 'Finance',
  doctype: 'account', title: 'Chart of Accounts', singular: 'Account', icon: ListTree,
  endpoint: '/accounting/accounts',
  columns: [
    codeCol('account_number', 'No.'),
    nameCol('name', 'Account', ListTree),
    { accessorKey: 'root_type', header: 'Root', cell: (i) => <StatusPill tone="neutral" withDot={false}>{humanizeEnum(i.getValue<string>())}</StatusPill> },
    { accessorKey: 'account_type', header: 'Type', cell: (i) => <span className="text-text-secondary">{humanizeEnum(i.getValue<string>()) || '—'}</span> },
    { accessorKey: 'account_currency', header: 'Currency', cell: (i) => <span className="font-mono text-dense text-text-secondary">{i.getValue<string>()}</span> },
  ],
};

const salesInvoices: DoctypeConfig = {
  slug: 'sales-invoices', modulePath: '/accounting', module: 'Sales',
  doctype: 'sales_invoice', title: 'Sales Invoices', singular: 'Sales Invoice', icon: Receipt,
  endpoint: '/accounting/sales-invoices',
  columns: [
    codeCol('name', 'No.'),
    dateCol('posting_date', 'Date'),
    partyCol('customer_id', 'Customer', 'customer_name'),
    moneyCol('grand_total', 'Grand total'),
    moneyCol('outstanding_amount', 'Outstanding'),
    docstatusCol,
  ],
};

// PO status pill — ERPNext-style "what to do next" labels so the list view
// reads as a worklist, not just a doctype dump.
function POStatusPill({ value }: { value: string }) {
  switch (value) {
    case 'Draft':                return <StatusPill tone="neutral" withDot={false}>Draft</StatusPill>;
    case 'To Receive and Bill':  return <StatusPill tone="warning" withDot={false}>To Receive &amp; Bill</StatusPill>;
    case 'To Receive':           return <StatusPill tone="info"    withDot={false}>To Receive</StatusPill>;
    case 'To Bill':              return <StatusPill tone="info"    withDot={false}>To Bill</StatusPill>;
    case 'Completed':            return <StatusPill tone="success" withDot={false}>Completed</StatusPill>;
    case 'On Hold':              return <StatusPill tone="warning" withDot={false}>On Hold</StatusPill>;
    case 'Closed':               return <StatusPill tone="neutral" withDot={false}>Closed</StatusPill>;
    case 'Stopped':              return <StatusPill tone="danger"  withDot={false}>Stopped</StatusPill>;
    case 'Cancelled':            return <StatusPill tone="danger"  withDot={false}>Cancelled</StatusPill>;
    default:                     return <StatusPill tone="neutral" withDot={false}>{value || '—'}</StatusPill>;
  }
}

function GRNStatusPill({ value }: { value: string }) {
  switch (value) {
    case 'Draft':         return <StatusPill tone="neutral" withDot={false}>Draft</StatusPill>;
    case 'To Bill':       return <StatusPill tone="warning" withDot={false}>To Bill</StatusPill>;
    case 'Completed':     return <StatusPill tone="success" withDot={false}>Completed</StatusPill>;
    case 'Return Issued': return <StatusPill tone="info"    withDot={false}>Return Issued</StatusPill>;
    case 'Cancelled':     return <StatusPill tone="danger"  withDot={false}>Cancelled</StatusPill>;
    default:              return <StatusPill tone="neutral" withDot={false}>{value || '—'}</StatusPill>;
  }
}

const purchaseReceipts: DoctypeConfig = {
  slug: 'purchase-receipts', modulePath: '/stock', module: 'Inventory',
  doctype: 'purchase_receipt', title: 'Goods Received (GR)', singular: 'Goods Received', icon: Package,
  endpoint: '/stock/purchase-receipts',
  columns: [
    codeCol('name', 'No.'),
    dateCol('posting_date', 'Received'),
    partyCol('supplier_id', 'Supplier', 'supplier_name'),
    { id: 'against_purchase_order', header: 'PO', cell: (i) => {
        const row = i.row.original as Record<string, unknown>;
        const name = (row.against_purchase_order_name as string) || '';
        const id   = (row.against_purchase_order_id as string) || '';
        if (name) return <span className="font-mono text-caption text-text-secondary">{name}</span>;
        return id ? <span className="font-mono text-caption text-text-secondary">{id.slice(-8)}</span> : <span className="text-text-tertiary">—</span>;
    } },
    moneyCol('total_value', 'Value'),
    { accessorKey: 'status', header: 'Status', cell: (i) => <GRNStatusPill value={i.getValue<string>()} /> },
  ],
};

function MRStatusPill({ value }: { value: string }) {
  switch (value) {
    case 'Draft':              return <StatusPill tone="neutral" withDot={false}>Draft</StatusPill>;
    case 'Pending':            return <StatusPill tone="warning" withDot={false}>Pending</StatusPill>;
    case 'Partially Ordered':  return <StatusPill tone="info"    withDot={false}>Partially</StatusPill>;
    case 'Ordered':            return <StatusPill tone="info"    withDot={false}>Ordered</StatusPill>;
    case 'Issued':             return <StatusPill tone="success" withDot={false}>Issued</StatusPill>;
    case 'Transferred':        return <StatusPill tone="success" withDot={false}>Transferred</StatusPill>;
    case 'Received':           return <StatusPill tone="success" withDot={false}>Received</StatusPill>;
    case 'Stopped':            return <StatusPill tone="danger"  withDot={false}>Stopped</StatusPill>;
    case 'Cancelled':          return <StatusPill tone="danger"  withDot={false}>Cancelled</StatusPill>;
    default:                   return <StatusPill tone="neutral" withDot={false}>{value || '—'}</StatusPill>;
  }
}

const materialRequests: DoctypeConfig = {
  slug: 'material-requests', modulePath: '/accounting', module: 'Procurement',
  doctype: 'material_request', title: 'Material Requests', singular: 'Material Request', icon: ClipboardList,
  endpoint: '/accounting/material-requests',
  columns: [
    codeCol('name', 'No.'),
    dateCol('transaction_date', 'Requested'),
    dateCol('required_by_date', 'Required by'),
    { accessorKey: 'purpose', header: 'Purpose', cell: (i) => {
        const v = i.getValue<string>();
        const label = v === 'purchase' ? 'Purchase'
          : v === 'material_transfer' ? 'Transfer'
          : v === 'material_issue' ? 'Issue'
          : v === 'manufacture' ? 'Manufacture' : v;
        return <StatusPill tone="neutral" withDot={false}>{label}</StatusPill>;
    } },
    { accessorKey: 'priority', header: 'Priority', cell: (i) => <MRPriorityPill value={i.getValue<string>()} /> },
    { accessorKey: 'status', header: 'Status', cell: (i) => <MRStatusPill value={i.getValue<string>()} /> },
  ],
};

function MRPriorityPill({ value }: { value: string }) {
  const v = value || 'normal';
  const tone = v === 'critical' ? 'danger' : v === 'urgent' ? 'warning' : 'neutral';
  const label = v.charAt(0).toUpperCase() + v.slice(1);
  return <StatusPill tone={tone} withDot={false}>{label}</StatusPill>;
}

const salesOrders: DoctypeConfig = {
  slug: 'sales-orders', modulePath: '/accounting', module: 'Sales',
  doctype: 'sales_order', title: 'Sales Orders', singular: 'Sales Order', icon: ClipboardList,
  endpoint: '/accounting/sales-orders',
  columns: [
    codeCol('name', 'No.'),
    dateCol('transaction_date', 'Order date'),
    dateCol('delivery_date', 'Delivery'),
    partyCol('customer_id', 'Customer', 'customer_name'),
    moneyCol('total', 'Total'),
    { accessorKey: 'status', header: 'Status', cell: (i) => <StatusPill tone="accent">{i.getValue<string>()}</StatusPill> },
    docstatusCol,
  ],
};

const purchaseOrders: DoctypeConfig = {
  slug: 'purchase-orders', modulePath: '/accounting', module: 'Procurement',
  doctype: 'purchase_order', title: 'Purchase Orders', singular: 'Purchase Order', icon: ClipboardList,
  endpoint: '/accounting/purchase-orders',
  columns: [
    codeCol('name', 'No.'),
    dateCol('transaction_date', 'Order date'),
    partyCol('supplier_id', 'Supplier', 'supplier_name'),
    moneyCol('grand_total', 'Grand total'),
    { accessorKey: 'status', header: 'Status', cell: (i) => <POStatusPill value={i.getValue<string>()} /> },
  ],
};

const purchaseInvoices: DoctypeConfig = {
  slug: 'purchase-invoices', modulePath: '/accounting', module: 'Procurement',
  doctype: 'purchase_invoice', title: 'Purchase Invoices', singular: 'Purchase Invoice', icon: ShoppingBag,
  endpoint: '/accounting/purchase-invoices',
  columns: [
    codeCol('name', 'No.'),
    dateCol('posting_date', 'Date'),
    partyCol('supplier_id', 'Supplier', 'supplier_name'),
    moneyCol('grand_total', 'Grand total'),
    moneyCol('outstanding_amount', 'Outstanding'),
    docstatusCol,
  ],
};

const paymentEntries: DoctypeConfig = {
  slug: 'payment-entries', modulePath: '/accounting', module: 'Finance',
  doctype: 'payment_entry', title: 'Payment', singular: 'Payment', icon: Wallet,
  endpoint: '/accounting/payment-entries',
  columns: [
    codeCol('name', 'No.'),
    dateCol('posting_date', 'Date'),
    { accessorKey: 'payment_type', header: 'Type', cell: (i) => {
        const v = i.getValue<string>();
        return v === 'receive'
          ? <StatusPill tone="success" withDot={false}>Receive</StatusPill>
          : v === 'pay'
            ? <StatusPill tone="info" withDot={false}>Pay</StatusPill>
            : <StatusPill tone="neutral" withDot={false}>Transfer</StatusPill>;
    } },
    moneyCol('paid_amount', 'Paid'),
    moneyCol('total_allocated_amount', 'Allocated'),
    docstatusCol,
  ],
};

const journalEntries: DoctypeConfig = {
  slug: 'journal-entries', modulePath: '/accounting', module: 'Finance',
  doctype: 'journal_entry', title: 'Journal Entries', singular: 'Journal Entry', icon: FileText,
  endpoint: '/accounting/journal-entries',
  columns: [
    codeCol('name', 'No.'),
    dateCol('posting_date', 'Date'),
    moneyCol('total_debit', 'Total debit'),
    moneyCol('total_credit', 'Total credit'),
    { accessorKey: 'user_remark', header: 'Remark', cell: (i) => <span className="text-text-secondary truncate">{i.getValue<string>() || '—'}</span> },
    docstatusCol,
  ],
};

const warehouses: DoctypeConfig = {
  slug: 'warehouses', modulePath: '/stock', module: 'Inventory',
  doctype: 'warehouse', title: 'Warehouses', singular: 'Warehouse', icon: Warehouse,
  endpoint: '/stock/warehouses',
  columns: [
    codeCol('code', 'Code'),
    nameCol('name', 'Warehouse', Warehouse),
    { accessorKey: 'warehouse_type', header: 'Type', cell: (i) => <span className="text-text-secondary">{humanizeEnum(i.getValue<string>()) || '—'}</span> },
    { accessorKey: 'is_group', header: 'Group?', cell: (i) => i.getValue<boolean>() ? <StatusPill tone="info" withDot={false}>Group</StatusPill> : <span className="text-text-tertiary">—</span> },
  ],
};

const employees: DoctypeConfig = {
  slug: 'employees', modulePath: '/hr', module: 'HR & Payroll',
  doctype: 'employee', title: 'Employees', singular: 'Employee', icon: Users,
  endpoint: '/hr/employees',
  columns: [
    codeCol('name'),
    nameCol('employee_name', 'Name', Users),
    { accessorKey: 'ptkp_status', header: 'PTKP', cell: (i) => <span className="font-mono text-dense text-text-secondary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'npwp', header: 'NPWP', cell: (i) => <span className="font-mono text-dense text-text-secondary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'status', header: 'Status', cell: (i) => <StatusPill tone={i.getValue<string>() === 'Active' ? 'success' : 'neutral'}>{i.getValue<string>()}</StatusPill> },
  ],
};

const projects: DoctypeConfig = {
  slug: 'projects', modulePath: '/projects', module: 'Operations',
  doctype: 'project', title: 'Projects', singular: 'Project', icon: Briefcase,
  endpoint: '/projects/projects',
  columns: [
    codeCol('name'),
    nameCol('project_name', 'Project', Briefcase),
    { accessorKey: 'status', header: 'Status', cell: (i) => <StatusPill tone={i.getValue<string>() === 'Completed' ? 'success' : 'accent'}>{i.getValue<string>()}</StatusPill> },
    moneyCol('total_billable_amount', 'Billable'),
    moneyCol('total_billed_amount', 'Billed'),
  ],
};

const assetLocations: DoctypeConfig = {
  slug: 'asset-locations', modulePath: '/assets', module: 'Assets',
  doctype: 'asset_location', title: 'Asset Locations', singular: 'Asset Location', icon: Building2,
  endpoint: '/assets/asset-locations',
  columns: [
    nameCol('name', 'Location', Building2),
    { accessorKey: 'parent_id', header: 'Parent', cell: (i) => {
        const v = i.getValue<string>();
        return v ? <span className="font-mono text-caption text-text-secondary">{v.slice(-8)}</span> : <span className="text-text-tertiary">—</span>;
    } },
    { accessorKey: 'is_group', header: 'Group?', cell: (i) => i.getValue<boolean>() ? <StatusPill tone="info" withDot={false}>Group</StatusPill> : <span className="text-text-tertiary">—</span> },
    { accessorKey: 'address', header: 'Address', cell: (i) => <span className="text-text-secondary truncate">{i.getValue<string>() || '—'}</span> },
  ],
};

const assetMovements: DoctypeConfig = {
  slug: 'asset-movements', modulePath: '/assets', module: 'Assets',
  doctype: 'asset_movement', title: 'Asset Movements', singular: 'Asset Movement', icon: ClipboardList,
  endpoint: '/assets/asset-movements',
  columns: [
    codeCol('name'),
    dateCol('movement_date', 'Date'),
    { accessorKey: 'movement_type', header: 'Type', cell: (i) => <StatusPill tone="info" withDot={false}>{i.getValue<string>()}</StatusPill> },
    { accessorKey: 'asset_id',     header: 'Asset',    cell: (i) => <span className="font-mono text-caption text-text-secondary">{(i.getValue<string>() || '').slice(-8)}</span> },
    { accessorKey: 'to_custodian', header: 'To custodian', cell: (i) => <span className="text-text-primary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'to_location',  header: 'To location',  cell: (i) => <span className="text-text-secondary">{i.getValue<string>() || '—'}</span> },
    docstatusCol,
  ],
};

const assetCategories: DoctypeConfig = {
  slug: 'asset-categories', modulePath: '/assets', module: 'Assets',
  doctype: 'asset_category', title: 'Asset Categories', singular: 'Asset Category', icon: Tag,
  endpoint: '/assets/asset-categories',
  columns: [
    nameCol('name', 'Category', Tag),
    { accessorKey: 'default_depreciation_method', header: 'Method',
      cell: (i) => <span className="text-text-secondary">{depreciationMethodLabel(i.getValue<string>())}</span> },
    { accessorKey: 'total_useful_life_months', header: 'Useful life (mo)',
      cell: (i) => <span className="num text-text-secondary">{i.getValue<number>()}</span>, meta: { align: 'right' } },
  ],
};

const assets: DoctypeConfig = {
  slug: 'assets', modulePath: '/assets', module: 'Assets',
  doctype: 'asset', title: 'Assets', singular: 'Asset', icon: ClipboardList,
  endpoint: '/assets/assets',
  columns: [
    codeCol('name'),
    nameCol('asset_name', 'Asset', ClipboardList),
    dateCol('purchase_date', 'Purchased'),
    moneyCol('gross_purchase_amount', 'Gross'),
    moneyCol('accumulated_depreciation', 'Acc. depreciation'),
    { accessorKey: 'status', header: 'Status', cell: (i) => <StatusPill tone="accent">{i.getValue<string>()}</StatusPill> },
  ],
  hasNew: false,
};

// ---- Generic CRUD masters ------------------------------------------------

const banks: DoctypeConfig = {
  slug: 'banks', modulePath: '/accounting', module: 'Finance',
  doctype: 'bank', title: 'Banks', singular: 'Bank', icon: Landmark,
  endpoint: '/accounting/banks',
  columns: [
    nameCol('name', 'Bank', Landmark),
    { accessorKey: 'swift_code', header: 'SWIFT', cell: (i) => <span className="font-mono text-dense text-text-secondary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'country', header: 'Country', cell: (i) => <span className="text-text-secondary">{i.getValue<string>() || '—'}</span> },
  ],
};

const taxCategories: DoctypeConfig = {
  slug: 'tax-categories', modulePath: '/accounting', module: 'Finance',
  doctype: 'tax_category', title: 'Tax Categories', singular: 'Tax Category', icon: Tag,
  endpoint: '/accounting/tax-categories',
  columns: [
    nameCol('name', 'Category', Tag),
    { accessorKey: 'description', header: 'Description', cell: (i) => <span className="text-text-secondary truncate">{i.getValue<string>() || '—'}</span> },
    dateCol('created_at', 'Created'),
  ],
};

const uoms: DoctypeConfig = {
  slug: 'uoms', modulePath: '/stock', module: 'Inventory',
  doctype: 'uom', title: 'Units of Measure', singular: 'UOM', icon: Ruler,
  endpoint: '/stock/uoms',
  columns: [
    nameCol('name', 'Name', Ruler),
    codeCol('code'),
    { accessorKey: 'conversion_factor', header: 'Conversion', meta: { align: 'right' },
      cell: (i) => <span className="num text-text-secondary">{i.getValue<string>() || '1'}</span> },
    { id: 'base_uom', header: 'Base UOM', cell: (i) => {
        const row = i.row.original as Record<string, unknown>;
        const name = (row.base_uom_name as string) || '';
        const id   = (row.base_uom_id as string) || '';
        if (name) return <span className="text-text-secondary">{name}</span>;
        return id ? <span className="font-mono text-caption text-text-secondary">{id.slice(-8)}</span> : <StatusPill tone="info" withDot={false}>Base</StatusPill>;
    } },
  ],
};

const financeBooks: DoctypeConfig = {
  slug: 'finance-books', modulePath: '/assets', module: 'Assets',
  doctype: 'finance_book', title: 'Finance Books', singular: 'Finance Book', icon: BookOpen,
  endpoint: '/assets/finance-books',
  columns: [
    nameCol('name', 'Book', BookOpen),
    { accessorKey: 'is_primary', header: 'Primary?', cell: (i) =>
        i.getValue<boolean>() ? <StatusPill tone="success" withDot={false}>Primary</StatusPill> : <span className="text-text-tertiary">—</span> },
    dateCol('created_at', 'Created'),
  ],
};

const assetValueAdjustments: DoctypeConfig = {
  slug: 'asset-value-adjustments', modulePath: '/assets', module: 'Assets',
  doctype: 'asset_value_adjustment', title: 'Asset Value Adjustments', singular: 'Asset Value Adjustment', icon: TrendingDown,
  endpoint: '/assets/asset-value-adjustments',
  columns: [
    codeCol('name', 'No.'),
    dateCol('adjustment_date', 'Date'),
    { accessorKey: 'kind', header: 'Kind', cell: (i) => {
        const v = i.getValue<string>();
        const label = v === 'revaluation' ? 'Revaluation ↑'
          : v === 'revaluation_down' ? 'Revaluation ↓'
          : v === 'impairment' ? 'Impairment' : v;
        const tone = v === 'revaluation' ? 'success' : v === 'impairment' ? 'danger' : 'warning';
        return <StatusPill tone={tone as never} withDot={false}>{label}</StatusPill>;
    } },
    moneyCol('amount', 'Amount'),
    { accessorKey: 'asset_id', header: 'Asset', cell: (i) => <span className="font-mono text-caption text-text-secondary">{(i.getValue<string>() || '').slice(-8)}</span> },
    docstatusCol,
  ],
};

const tasks: DoctypeConfig = {
  slug: 'tasks', modulePath: '/projects', module: 'Operations',
  doctype: 'task', title: 'Tasks', singular: 'Task', icon: CheckSquare,
  endpoint: '/projects/tasks',
  columns: [
    nameCol('subject', 'Subject', CheckSquare),
    { accessorKey: 'status', header: 'Status', cell: (i) => {
        const v = i.getValue<string>();
        return v === 'Completed' ? <StatusPill tone="success">{v}</StatusPill>
             : v === 'Cancelled' ? <StatusPill tone="danger">{v}</StatusPill>
             : v === 'Working'   ? <StatusPill tone="accent">{v}</StatusPill>
             : <StatusPill tone="neutral">{v || 'Open'}</StatusPill>;
    } },
    { accessorKey: 'priority', header: 'Priority', cell: (i) => {
        const v = i.getValue<string>();
        return v === 'Urgent' ? <StatusPill tone="danger">{v}</StatusPill>
             : v === 'High'   ? <StatusPill tone="warning">{v}</StatusPill>
             : <StatusPill tone="neutral">{v || 'Medium'}</StatusPill>;
    } },
    dateCol('due_date', 'Due'),
    { accessorKey: 'assigned_to', header: 'Assignee', cell: (i) => <span className="text-text-secondary">{i.getValue<string>() || '—'}</span> },
  ],
};

const taskTemplates: DoctypeConfig = {
  slug: 'task-templates', modulePath: '/projects', module: 'Operations',
  doctype: 'task_template', title: 'Task Templates', singular: 'Task Template', icon: ListChecks,
  endpoint: '/projects/task-templates',
  columns: [
    nameCol('name', 'Template', ListChecks),
    { accessorKey: 'description', header: 'Description', cell: (i) => <span className="text-text-secondary truncate">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'items', header: 'Items', meta: { align: 'right' },
      cell: (i) => <span className="num text-text-secondary">{Array.isArray(i.getValue()) ? (i.getValue() as unknown[]).length : '—'}</span> },
  ],
};

const timesheets: DoctypeConfig = {
  slug: 'timesheets', modulePath: '/projects', module: 'Operations',
  doctype: 'timesheet', title: 'Timesheets', singular: 'Timesheet', icon: Clock,
  endpoint: '/projects/timesheets',
  columns: [
    codeCol('name', 'No.'),
    dateCol('start_date', 'Start'),
    dateCol('end_date',   'End'),
    { accessorKey: 'total_hours', header: 'Hours', meta: { align: 'right' },
      cell: (i) => <span className="num">{i.getValue<string>() || '0'}</span> },
    moneyCol('total_billable_amount', 'Billable'),
    docstatusCol,
  ],
};

// ---- Asset Operations vertical -------------------------------------------

// Status pill for the maintenance_request status machine
// (open → in_progress → closed; open/in_progress → cancelled).
function MaintenanceStatusPill({ value }: { value: string }) {
  switch (value) {
    case 'open':        return <StatusPill tone="warning" withDot={false}>Open</StatusPill>;
    case 'in_progress': return <StatusPill tone="info"    withDot={false}>In progress</StatusPill>;
    case 'closed':      return <StatusPill tone="success" withDot={false}>Closed</StatusPill>;
    case 'cancelled':   return <StatusPill tone="danger"  withDot={false}>Cancelled</StatusPill>;
    default:            return <StatusPill tone="neutral" withDot={false}>{value || '—'}</StatusPill>;
  }
}

// Status pill for the asset_work_order status machine
// (draft → in_progress → completed, or cancelled).
function WorkOrderStatusPill({ value }: { value: string }) {
  switch (value) {
    case 'draft':       return <StatusPill tone="neutral" withDot={false}>Draft</StatusPill>;
    case 'in_progress': return <StatusPill tone="info"    withDot={false}>In progress</StatusPill>;
    case 'completed':   return <StatusPill tone="success" withDot={false}>Completed</StatusPill>;
    case 'cancelled':   return <StatusPill tone="danger"  withDot={false}>Cancelled</StatusPill>;
    default:            return <StatusPill tone="neutral" withDot={false}>{value || '—'}</StatusPill>;
  }
}

function PlacementStatusPill({ value }: { value: string }) {
  switch (value) {
    case 'active': return <StatusPill tone="success" withDot={false}>Active</StatusPill>;
    case 'ended':  return <StatusPill tone="neutral" withDot={false}>Ended</StatusPill>;
    default:       return <StatusPill tone="neutral" withDot={false}>{value || '—'}</StatusPill>;
  }
}

const maintenanceRequests: DoctypeConfig = {
  slug: 'maintenance-requests', modulePath: '/maintenance', module: 'Asset Operations',
  doctype: 'maintenance_request', title: 'Maintenance Requests', singular: 'Maintenance Request', icon: Wrench,
  endpoint: '/maintenance/maintenance-requests',
  columns: [
    codeCol('name', 'No.'),
    partyCol('asset_id', 'Asset', 'asset_name'),
    { accessorKey: 'request_type', header: 'Type', cell: (i) => {
        const v = i.getValue<string>();
        const label = v === 'breakdown' ? 'Breakdown' : v === 'preventive' ? 'Preventive' : v === 'corrective' ? 'Corrective' : v;
        return <StatusPill tone="neutral" withDot={false}>{label}</StatusPill>;
    } },
    { accessorKey: 'priority', header: 'Priority', cell: (i) => {
        const v = i.getValue<string>();
        return v === 'urgent' ? <StatusPill tone="danger">Urgent</StatusPill>
             : v === 'high'   ? <StatusPill tone="warning">High</StatusPill>
             : v === 'low'    ? <StatusPill tone="neutral">Low</StatusPill>
             : <StatusPill tone="info">Medium</StatusPill>;
    } },
    { accessorKey: 'status', header: 'Status', cell: (i) => <MaintenanceStatusPill value={i.getValue<string>()} /> },
    dateCol('created_at', 'Created'),
  ],
};

const assetWorkOrders: DoctypeConfig = {
  slug: 'asset-work-orders', modulePath: '/maintenance', module: 'Asset Operations',
  doctype: 'asset_work_order', title: 'Asset Work Orders', singular: 'Asset Work Order', icon: ClipboardList,
  endpoint: '/maintenance/asset-work-orders',
  columns: [
    codeCol('name', 'No.'),
    { accessorKey: 'wo_type', header: 'Type', cell: (i) => {
        const t = i.getValue<string>();
        const sub = i.row.original.wo_subtype as string | undefined;
        return <span className="inline-flex items-center gap-1.5">
          <StatusPill tone="accent" withDot={false}>{humanizeEnum(t)}</StatusPill>
          {sub && <span className="text-caption text-text-tertiary">{humanizeEnum(sub)}</span>}
        </span>;
    } },
    partyCol('asset_id', 'Asset', 'asset_name'),
    { accessorKey: 'status', header: 'Status', cell: (i) => <WorkOrderStatusPill value={i.getValue<string>()} /> },
    moneyCol('total_cost', 'Total cost'),
    dateCol('created_at', 'Created'),
  ],
};

const assetMeterReadings: DoctypeConfig = {
  slug: 'asset-meter-readings', modulePath: '/assets', module: 'Assets',
  doctype: 'asset_meter_reading', title: 'Meter Readings', singular: 'Meter Reading', icon: Gauge,
  endpoint: '/assets/asset-meter-readings',
  columns: [
    codeCol('name', 'No.'),
    partyCol('asset_id', 'Asset', 'asset_name'),
    { accessorKey: 'meter_type', header: 'Meter', cell: (i) => {
        const v = i.getValue<string>();
        const label = v === 'operating_hours' ? 'Operating hours' : v === 'odometer_km' ? 'Odometer (km)' : v === 'fuel_litre' ? 'Fuel (litre)' : v;
        return <StatusPill tone="neutral" withDot={false}>{label}</StatusPill>;
    } },
    { accessorKey: 'value', header: 'Value', meta: { align: 'right' },
      cell: (i) => <span className="num text-text-secondary">{i.getValue<string>()}</span> },
    dateCol('reading_date', 'Reading date'),
  ],
};

const assetAllocations: DoctypeConfig = {
  slug: 'asset-allocations', modulePath: '/assets', module: 'Assets',
  doctype: 'asset_allocation', title: 'Asset Allocations', singular: 'Asset Allocation', icon: Boxes,
  endpoint: '/assets/asset-allocations',
  columns: [
    codeCol('name', 'No.'),
    partyCol('asset_id', 'Asset', 'asset_name'),
    partyCol('project_id', 'Project', 'project_name'),
    { accessorKey: 'status', header: 'Status', cell: (i) => <PlacementStatusPill value={i.getValue<string>()} /> },
    dateCol('start_date', 'Start'),
    dateCol('end_date', 'End'),
  ],
};

const employeeAssignments: DoctypeConfig = {
  slug: 'employee-assignments', modulePath: '/hr', module: 'HR & Payroll',
  doctype: 'employee_assignment', title: 'Employee Assignments', singular: 'Employee Assignment', icon: UserCheck,
  endpoint: '/hr/employee-assignments',
  columns: [
    codeCol('name', 'No.'),
    partyCol('employee_id', 'Employee', 'employee_name'),
    partyCol('project_id', 'Project', 'project_name'),
    { accessorKey: 'role', header: 'Role', cell: (i) => <span className="text-text-secondary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'status', header: 'Status', cell: (i) => <PlacementStatusPill value={i.getValue<string>()} /> },
    dateCol('start_date', 'Start'),
  ],
};

const payrollEntitySplits: DoctypeConfig = {
  slug: 'payroll-entity-splits', modulePath: '/hr', module: 'HR & Payroll',
  doctype: 'payroll_entity_split', title: 'Payroll Entity Splits', singular: 'Payroll Entity Split', icon: SplitSquareHorizontal,
  endpoint: '/hr/payroll-entity-splits',
  columns: [
    partyCol('employee_id', 'Employee', 'employee_name'),
    { accessorKey: 'company_name', header: 'Entity', cell: (i) => <span className="text-text-secondary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'salary_slip_name', header: 'Salary slip', cell: (i) => <span className="text-text-secondary">{i.getValue<string>() || '—'}</span> },
    { accessorKey: 'percentage', header: 'Percentage', meta: { align: 'right' },
      cell: (i) => <span className="num text-text-secondary">{i.getValue<string>()}%</span> },
    moneyCol('amount', 'Amount'),
  ],
};

const deliveryNotes: DoctypeConfig = {
  slug: 'delivery-notes', modulePath: '/accounting', module: 'Sales',
  doctype: 'delivery_note', title: 'Delivery Notes', singular: 'Delivery Note', icon: Truck,
  endpoint: '/accounting/delivery-notes',
  columns: [
    codeCol('name', 'No.'),
    partyCol('customer_id', 'Customer', 'customer_name'),
    { id: 'sales_order', header: 'Sales order', cell: (i) => {
        const row = i.row.original as Record<string, unknown>;
        const name = (row.sales_order_name as string) || '';
        const id   = (row.sales_order_id as string) || '';
        if (name) return <span className="font-mono text-caption text-text-secondary">{name}</span>;
        return id ? <span className="font-mono text-caption text-text-secondary">{id.slice(-8)}</span> : <span className="text-text-tertiary">—</span>;
    } },
    { accessorKey: 'status', header: 'Status', cell: (i) => <GRNStatusPill value={i.getValue<string>()} /> },
    dateCol('posting_date', 'Posting date'),
  ],
};

const stockEntries: DoctypeConfig = {
  slug: 'stock-entries', modulePath: '/stock', module: 'Inventory',
  doctype: 'stock_entry', title: 'Stock Entries', singular: 'Stock Entry', icon: Boxes,
  endpoint: '/stock/stock-entries',
  columns: [
    codeCol('name', 'No.'),
    { accessorKey: 'purpose', header: 'Purpose', cell: (i) => <span className="text-text-secondary capitalize">{(i.getValue<string>() || '').replace(/_/g, ' ')}</span> },
    { accessorKey: 'docstatus', header: 'Status', cell: (i) => { const d = i.getValue<number>(); return <span className="text-text-secondary">{d === 1 ? 'Submitted' : d === 2 ? 'Cancelled' : 'Draft'}</span>; } },
    dateCol('posting_date', 'Posting date'),
  ],
};

const salarySlips: DoctypeConfig = {
  slug: 'salary-slips', modulePath: '/hr', module: 'HR & Payroll',
  doctype: 'salary_slip', title: 'Salary Slips', singular: 'Salary Slip', icon: Receipt,
  endpoint: '/hr/salary-slips',
  columns: [
    codeCol('name', 'No.'),
    partyCol('employee_id', 'Employee', 'employee_name'),
    dateCol('posting_date', 'Posting date'),
    moneyCol('gross_pay', 'Gross pay'),
    moneyCol('net_pay', 'Net pay'),
  ],
};

const bom: DoctypeConfig = {
  slug: 'boms', modulePath: '/manufacturing', module: 'Manufacturing',
  doctype: 'bom', title: 'BOMs', singular: 'BOM', icon: Layers,
  endpoint: '/manufacturing/boms',
  hasNew: false,
  columns: [
    codeCol('name', 'No.'),
    partyCol('item_id', 'Item', 'item_name'),
    { accessorKey: 'quantity', header: 'Qty', meta: { align: 'right' },
      cell: (i) => <span className="num text-text-secondary">{i.getValue<string>()}</span> },
    moneyCol('total_cost', 'Total cost'),
    { accessorKey: 'docstatus', header: 'Status', cell: (i) => <DocstatusPill docstatus={i.getValue<number>()} /> },
  ],
};

const workOrders: DoctypeConfig = {
  slug: 'work-orders', modulePath: '/manufacturing', module: 'Manufacturing',
  doctype: 'work_order', title: 'Work Orders', singular: 'Work Order', icon: Factory,
  endpoint: '/manufacturing/work-orders',
  hasNew: false,
  columns: [
    codeCol('name', 'No.'),
    partyCol('item_id', 'Item', 'item_name'),
    { accessorKey: 'qty', header: 'Qty', meta: { align: 'right' },
      cell: (i) => <span className="num text-text-secondary">{i.getValue<string>()}</span> },
    { accessorKey: 'status', header: 'Status',
      cell: (i) => <StatusPill tone="neutral" withDot={false}>{i.getValue<string>() || '—'}</StatusPill> },
    { accessorKey: 'docstatus', header: 'Doc status', cell: (i) => <DocstatusPill docstatus={i.getValue<number>()} /> },
  ],
};

export const doctypes: Record<string, DoctypeConfig> = {
  items, customers, suppliers, taxTemplates, accounts,
  salesInvoices, salesOrders, materialRequests, purchaseOrders, purchaseReceipts, purchaseInvoices, paymentEntries, journalEntries,
  warehouses, stockEntries,
  employees, projects, assets, assetCategories, assetLocations, assetMovements,
  banks, taxCategories, uoms,
  financeBooks, assetValueAdjustments,
  tasks, taskTemplates, timesheets,
  maintenanceRequests, assetWorkOrders,
  bom, workOrders,
  assetMeterReadings, assetAllocations,
  employeeAssignments, payrollEntitySplits, salarySlips,
  deliveryNotes,
};

// Pretty-prints the four depreciation method enum values for tables + forms.
export function depreciationMethodLabel(m: string): string {
  switch (m) {
    case 'straight_line':      return 'Straight line';
    case 'written_down_value': return 'Written down value';
    case 'declining_balance':  return 'Double declining';
    case 'manual':             return 'Manual';
    default:                   return m || '—';
  }
}

// Module index — for the module landing pages. Each doctype has a single home
// module to avoid the same card appearing under several modules. `links` are
// non-doctype destinations surfaced on the module page (e.g. dashboards).
type ModuleLink = { to: string; title: string; description: string; icon: LucideIcon };
export const modules: { path: string; name: string; icon: LucideIcon; doctypes: DoctypeConfig[]; description: string; links?: ModuleLink[] }[] = [
  {
    path: '/accounting', name: 'Finance', icon: Wallet,
    description: 'Payments, journals, accounts, taxes, and the books.',
    links: [{ to: '/accounting/reports', title: 'Financial Reports', description: 'P&L, Balance Sheet, Cash Flow, Trial Balance, AR/AP ageing, PPN, General Ledger, Stock Balance.', icon: FileBarChart }],
    doctypes: [paymentEntries, journalEntries, accounts, banks, taxTemplates, taxCategories],
  },
  {
    path: '/stock', name: 'Inventory', icon: Warehouse,
    description: 'Items, warehouses, receipts, and stock movements.',
    doctypes: [items, warehouses, stockEntries, purchaseReceipts, uoms],
  },
  {
    path: '/buying', name: 'Procurement', icon: ShoppingBag,
    description: 'Material requests, purchase orders, bills, and suppliers.',
    doctypes: [materialRequests, purchaseOrders, purchaseInvoices, suppliers],
  },
  {
    path: '/selling', name: 'Sales', icon: BarChart3,
    description: 'Sales orders, deliveries, invoices, and customers.',
    doctypes: [salesOrders, salesInvoices, deliveryNotes, customers],
  },
  {
    path: '/hr', name: 'HR & Payroll', icon: Users,
    description: 'Employees, assignments, payroll, and cross-entity splits.',
    doctypes: [employees, employeeAssignments, salarySlips, payrollEntitySplits],
  },
  {
    path: '/projects', name: 'Operations', icon: Briefcase,
    description: 'Projects, tasks, and timesheet billing.',
    doctypes: [projects, tasks, taskTemplates, timesheets],
  },
  {
    path: '/maintenance', name: 'Asset Operations', icon: Wrench,
    description: 'Breakdown reports and repair work orders for heavy-equipment assets.',
    doctypes: [maintenanceRequests, assetWorkOrders],
  },
  {
    path: '/assets', name: 'Assets', icon: ClipboardList,
    description: 'Fixed-asset register, depreciation, movements, locations, meters, allocations.',
    links: [{ to: '/operations/overview', title: 'Operations Overview', description: 'Asset availability, utilisation, and maintenance pipeline.', icon: Gauge }],
    doctypes: [assets, assetCategories, assetLocations, assetMovements, assetMeterReadings, assetAllocations],
  },
  {
    path: '/manufacturing', name: 'Manufacturing', icon: Factory,
    description: 'BOMs and production / reman work orders.',
    doctypes: [bom, workOrders],
  },
];

// Convenience: get config by slug+module path.
export function getDoctype(modulePath: string, slug: string): DoctypeConfig | undefined {
  return Object.values(doctypes).find((d) => d.modulePath === modulePath && d.slug === slug);
}

// Re-export for sidebar / starred shortcuts
export { Sparkles, Settings };
